/* One instrument, from the opening landscape to the interactive turntable. */
(() => {
  'use strict';
  const clamp = n => Math.max(0, Math.min(1, n));
  const mix = (a, b, t) => a + (b - a) * t;
  const ease = t => 1 - (1 - t) ** 4;
  const soften = t => t * t * (3 - 2 * t);
  class HeroArrival {
    constructor(hero, cycle) {
      this.hero = hero; this.cycle = cycle;
      this.visual = cycle.querySelector('.light-visual');
      this.scene = cycle.querySelector('.light-scene');
      this.content = hero.querySelector('.hero-content');
      this.title = hero.querySelector('h1');
      this.rating = hero.querySelector('.hero-rating');
      this.introduction = hero.querySelector('.hero-introduction');
      this.pauseButton = hero.querySelector('[data-hero-pause]');
      this.abort = new AbortController();
      const initialTarget = document.getElementById(location.hash.slice(1));
      this.initialAnchor = !!initialTarget && !hero.contains(initialTarget);
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.progress = 0; this.elapsed = 0; this.waited = 0; this.raf = 0;
      this.started = false; this.finished = false; this.last = 0;
      this.reveal = 0; this.driftElapsed = 0; this.paused = false; this.idleTimer = 0;
      this.exploded = window.DMExplodedBridge ? new window.DMExplodedBridge(cycle, () => this.schedule()) : null;
      this.onFrame = event => { if (!this.decoded && event.detail.hero) { this.decoded = true; this.schedule(); } };
      const on = { signal: this.abort.signal };
      document.addEventListener('dm:intro-end', () => { this.last = 0; this.schedule(); }, on);
      document.addEventListener('dm:intro-start', () => {
        cancelAnimationFrame(this.raf); clearTimeout(this.idleTimer); this.raf = 0; this.idleTimer = 0; this.last = 0;
      }, on);
      if (this.initialAnchor) {
        // Native smooth anchor arrival crosses the hero; do not load an effect for that transit.
        const release = () => { this.initialAnchor = false; };
        document.addEventListener('scrollend', release, { ...on, once:true });
        for (const type of ['wheel','touchstart','keydown']) addEventListener(type, release, { ...on, passive:true, once:true });
      }
      cycle.addEventListener('dm:frame-painted', this.onFrame, on);
      this.pauseButton?.addEventListener('click', () => cycle.querySelector('dm-rhythm')?.setPaused(!this.paused), on);
      cycle.addEventListener('dm:rhythm-pause', event => {
        this.paused = event.detail.paused;
        this.pauseButton.textContent = this.paused ? '▷' : 'Ⅱ';
        this.pauseButton.setAttribute('aria-pressed', String(this.paused));
        this.pauseButton.setAttribute('aria-label', this.paused ? window.DM.ui.se5382ea42c78 : window.DM.ui.sb431141880f3);
        this.schedule();
      }, on);
      addEventListener('scroll', () => this.schedule(), { ...on, passive: true });
      addEventListener('resize', () => this.measure(), { ...on, passive: true });
      document.addEventListener('visibilitychange', () => {
        this.last = 0;
        if (document.hidden) { cancelAnimationFrame(this.raf); clearTimeout(this.idleTimer); this.idleTimer = 0; this.raf = 0; }
        else this.schedule();
      }, on);
      this.motion.addEventListener('change', () => this.preferences(), on);
      document.addEventListener('dm:time', () => {
        const disabled = this.motion.matches || !!window.DMSun?.state?.fixed;
        if (disabled !== this.disabled) this.preferences();
      }, on);
      this.resize = new ResizeObserver(() => this.measure());
      this.resize.observe(hero); this.resize.observe(this.scene);
      document.fonts.ready.then(() => { if (!this.abort.signal.aborted) this.measure(); });
      this.preferences();
    }
    preferences() {
      this.disabled = this.motion.matches || !!window.DMSun?.state?.fixed;
      if (this.disabled) {
        this.exploded?.hide();
        cancelAnimationFrame(this.raf); clearTimeout(this.idleTimer); this.idleTimer = 0; this.raf = 0; this.last = 0;
        delete this.hero.dataset.arrivalEnabled;
        delete this.hero.dataset.arrivalState;
        delete this.cycle.dataset.arrivalFloating;
        this.cycle.setHeroActive(false);
        this.cycle.setHeroFrame(null); this.setSignal(false);
        if (this.pauseButton) this.pauseButton.hidden = true;
      } else {
        this.hero.dataset.arrivalEnabled = 'true';
        this.measure();
      }
    }
    measure() {
      if (this.disabled) return;
      const h = this.hero.offsetHeight, w = innerWidth, mobile = w <= 700, compact = mobile && h < 800;
      const rect = this.hero.getBoundingClientRect();
      this.heroTop = rect.top + scrollY;
      this.cycleTop = this.cycle.getBoundingClientRect().top + scrollY;
      const content = this.content;
      const center = content.offsetLeft + content.offsetWidth / 2;
      const top = el => content.offsetTop + el.offsetTop;
      const targetCenter = w * (mobile ? .5 : .32);
      const ratingHeight = this.rating.offsetHeight;
      const meta = this.hero.querySelector('.hero-meta');
      // The mobile rating has two lines: reserve its measured height between meta and title.
      const metaBottom = meta ? meta.offsetTop + meta.offsetHeight : 0;
      const titleTop = mobile ? Math.max(compact ? 150 : 157, metaBottom + 12 + ratingHeight + 12) : h * .28;
      const scale = mobile ? .79 : .76;
      const titleBottom = titleTop + this.title.offsetHeight * scale;
      const lower = this.hero.querySelector('.hero-lower');
      const copyTop = mobile ? Math.min(h * (compact ? .565 : .605),
        (lower ? lower.offsetTop : h - 120) - this.introduction.offsetHeight - 16) : titleBottom + 12;
      const vars = {
        '--arrival-title-x': `${targetCenter - center}px`,
        '--arrival-title-y': `${titleTop - top(this.title)}px`,
        '--arrival-title-scale': scale - 1,
        '--arrival-copy-x': `${targetCenter - center}px`,
        '--arrival-copy-y': `${copyTop - top(this.introduction)}px`,
        '--arrival-rating-x': `${targetCenter - center}px`,
        '--arrival-rating-y': `${titleTop - (mobile ? ratingHeight + 12 : 40) - top(this.rating)}px`
      };
      for (const [key, value] of Object.entries(vars)) this.hero.style.setProperty(key, value);
      // Rest poses 0–2 occupy y=.24–.81 of their transparent square. Fit that
      // visible instrument between the measured title and copy, including its shadow.
      const size = mobile ? Math.min(w * (compact ? .72 : .80), 410,
        Math.max(1, copyTop - titleBottom - 28) / .57) : Math.min(w * .46, h * .78, 730);
      this.start = { size, x: w * (mobile ? .5 : .70) - size / 2,
        y: mobile ? titleBottom + 14 - size * .24 : h * .63 - size * .48 };
      this.schedule();
    }
    schedule() {
      clearTimeout(this.idleTimer); this.idleTimer = 0;
      if (!this.disabled && !this.raf && !document.hidden && !document.documentElement.classList.contains('dm-intro-on')) this.raf = requestAnimationFrame(t => this.tick(t));
    }
    setSignal(visible) {
      if (this.cycle.heroSignalVisible === visible) return;
      this.cycle.heroSignalVisible = visible;
      this.cycle.dispatchEvent(new CustomEvent('dm:hero-presence'));
    }
    tick(now) {
      this.raf = 0;
      const dt = this.last ? Math.min(80, now - this.last) : 0;
      this.last = now;
      const y = scrollY, inHero = y < this.cycleTop && y + innerHeight > this.heroTop;
      // Restored scroll positions and anchor arrivals never pull the instrument out of its section.
      const travel = clamp((y - this.heroTop) / (this.cycleTop - this.heroTop));
      const t = soften(travel);
      // Measure before this tick's style writes, avoiding a forced layout during the flight.
      const sceneRect = inHero ? this.scene.getBoundingClientRect() : null;
      if (!inHero) { this.progress = 1; this.reveal = 1; this.finished = true; this.initialAnchor = false; }
      if (inHero) {
        if (!this.paused && !navigator.connection?.saveData && this.reveal > 0) this.driftElapsed += dt;
        const restFrame = 1 - Math.cos(this.driftElapsed / 18000 * Math.PI * 2);
        this.cycle.setHeroFrame(mix(restFrame, this.cycle.heroUrls.length - 1, t));
        if (!this.cycle.heroVisible) this.cycle.setHeroActive(true);
        this.decoded ||= this.cycle.dataset.arrivalFrame !== undefined;
        if(this.decoded && !this.initialAnchor)this.exploded?.prepare();
        if (!this.finished && this.decoded) {
          this.waited += dt;
          if (this.waited >= 1500 || y > this.heroTop + 35) this.started = true;
          if (this.started) {
            this.elapsed += dt;
            this.progress = soften(clamp(this.elapsed / 2300));
            this.reveal = soften(clamp((this.elapsed - 240) / 2600));
            if (this.elapsed >= 2840) { this.finished = true; this.progress = 1; this.reveal = 1; }
          }
        }
      } else if (this.cycle.heroVisible) { this.cycle.setHeroActive(false); this.cycle.setHeroFrame(null); }
      const p = Math.max(this.progress, ease(clamp((y - this.heroTop) / ((this.cycleTop - this.heroTop) * .35))));
      const opacity = Math.max(this.reveal, soften(clamp(travel / .35)));
      this.hero.style.setProperty('--arrival', p.toFixed(5));
      this.hero.dataset.arrivalState = this.finished ? 'settled' : this.started ? 'revealing' : 'waiting';
      if (this.pauseButton) this.pauseButton.hidden = opacity < .92;
      const floating = inHero && this.decoded;
      if (floating) {
        const slot = sceneRect;
        const size = Math.min(slot.width, slot.height, 900);
        const end = { size, x: slot.left + (slot.width - size) / 2,
          y: slot.top - Math.max(0, this.cycleTop - y) + (slot.height - size) / 2 };
        const s = mix(this.start.size * mix(.992, 1, opacity), end.size, t);
        const x = mix(this.start.x, end.x, t);
        const vy = mix(this.start.y + (1 - opacity) * 7 - (y - this.heroTop) * .10, end.y, t);
        this.visual.style.setProperty('--arrival-visual-x', `${x.toFixed(3)}px`);
        this.visual.style.setProperty('--arrival-visual-y', `${vy.toFixed(3)}px`);
        this.visual.style.setProperty('--arrival-visual-scale', (s / 1000).toFixed(6));
        this.visual.style.setProperty('--arrival-visual-opacity', opacity.toFixed(5));
        this.visual.style.setProperty('--arrival-visual-blur', opacity < 1 ? `${((1-opacity)*2.5).toFixed(2)}px` : '0px');
        this.cycle.style.setProperty('--arrival-dock', clamp((travel - .55) / .45).toFixed(4));
        this.cycle.dataset.arrivalFloating = 'true';
        if (!this.initialAnchor) this.exploded?.update(travel, {x,y:vy,size:s}, opacity);
      } else { delete this.cycle.dataset.arrivalFloating; this.exploded?.hide(); }
      this.setSignal(inHero && opacity > .92 && !this.exploded?.visible);
      if (inHero && !this.finished && this.decoded) this.schedule();
      else if (inHero && this.decoded && !this.paused && !navigator.connection?.saveData && !this.exploded?.visible) this.idleTimer = setTimeout(() => { this.idleTimer = 0; this.schedule(); }, 40);
      else this.last = 0;
    }
    destroy() {
      this.abort.abort(); this.resize.disconnect(); cancelAnimationFrame(this.raf); clearTimeout(this.idleTimer);
      this.exploded?.destroy();
      delete this.hero.dataset.arrivalEnabled; delete this.hero.dataset.arrivalState;
      delete this.cycle.dataset.arrivalFloating; this.cycle.setHeroActive(false); this.cycle.setHeroFrame(null); this.setSignal(false);
      if (this.pauseButton) this.pauseButton.hidden = true;
    }
  }
  let arrival;
  function init() {
    arrival?.destroy(); arrival = null;
    const hero = document.querySelector('.hero'), cycle = document.querySelector('dm-light-cycle[data-turntable]');
    if (hero && cycle?.querySelector('.light-visual') && hero.closest('.shopify-section')?.nextElementSibling?.contains(cycle)) {
      arrival = new HeroArrival(hero, cycle);
    }
  }
  init();
  document.addEventListener('shopify:section:load', init);
  document.addEventListener('shopify:section:unload', () => requestAnimationFrame(init));
  document.addEventListener('shopify:section:reorder', init);
})();
